import React, { ComponentProps, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import BlogNavigation from '@tovia/man-ui/lib/components/Blog/BlogNavigation';
import { useParams, useHistory } from 'react-router';
import moment from 'moment';

import { PostItem } from 'src/client/components/Blog/PostItem';
import LoaderWrapper from 'src/client/components/Loader/LoaderWrapper';
import { load } from 'src/client/redux/modules/blogPost';
import { renderPostItemProps } from 'src/client/utils/posts';
import { useSelector } from 'src/client/redux/modules/helpers/useSelector';
import Helmet from '../../Helmet/Helmet';
import urls from 'src/shared/urls';
import { isPrerender } from 'src/client/utils/prerenderDetect';
import { CommentSection } from '../../CommentSection/CommentSection';
import { PreviousNextBlog } from 'src/@types/Blog';

type BlogNavigationProps = ComponentProps<typeof BlogNavigation>;

const Div = styled.div`
  & .content {
    a {
      text-decoration: underline;
    }
  }
`;

export default function Post() {
  const dispatch = useDispatch();
  const { blogNavigation } = useSelector((state) => state.app.config.components);

  const { item, loading, error } = useSelector((state) => state.blogPost);
  const timezone = useSelector((state) => state.app.timezone);
  const site = useSelector((state) => state.site);

  const { name, date } = useParams<{ name: string; date: string }>();
  const history = useHistory();

  useEffect(() => {
    if (!isPrerender && item) {
      window.dataLayer.push({
        event: 'select_content',
        content_type: 'blog',
        item_id: item.title,
      });
    }
  }, [item]);

  const loadData = useCallback(() => {
    const loadParams = {
      name,
      date,
    };

    dispatch(load(loadParams));
  }, [dispatch, name, date]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (error?.status === 404) {
      history.replace(urls.get.notFound);
    }
  }, [error, history]);

  const renderBlogNavigation = (direction: BlogNavigationProps['direction']) => {
    const object: PreviousNextBlog | undefined = item?.[`${direction}Blog`];

    if (object && Object.keys(object).length > 0) {
      return <BlogNavigation direction={direction} title={object.title} url={object.path} />;
    }

    return null;
  };

  const displayNavigation = () => {
    if (blogNavigation?.component === 'BlogNavigation') {
      return (
        <>
          {renderBlogNavigation('previous')}
          {renderBlogNavigation('next')}
        </>
      );
    }

    return null;
  };

  if (item && !loading && !error) {
    const defaultDescription =
      item.description && item.description.trim() !== '' && item.description.trim() !== '0'
        ? item.description
        : `${item.title} - ${item.content
            .replace(/(<([^>]+)>)/gi, '')
            .substr(0, 150)
            .trim()
            .replace(/\n\s*\n/g, '\n')
            .replace(/\t\t*\t/gi, '')}...`;

    return (
      <Div className="pull-left col-lg-8 col-md-8 col-sm-12 col-xs-12">
        <Helmet
          id="blogPost"
          variables={{
            postName: item.title,
            date: moment(item.publishedAt).format('MMMM Do YYYY'),
            siteName: site.name,
          }}
          defaultTitle="{{{postName}}} - {{date}} - {{siteName}}"
          defaultDescription={defaultDescription}
        />
        <PostItem
          {...renderPostItemProps({
            item,
            timezone,
            // className,
            isSinglePost: true,
          })}
        />
        {displayNavigation()}

        <div className="clear" />
        <CommentSection
          id="comment-section"
          objectUUID={item.UUID}
          parentItem={item}
          parentItemType="blogPost"
          campaign="blog-comment"
        />
      </Div>
    );
  }

  return (
    <div className="pull-left col-lg-8 col-md-8 col-sm-12 col-xs-12">
      <LoaderWrapper defaultHeight reloadFunction={loadData} loadingStatus={loading} reduxErrorStatus={error} />
    </div>
  );
}
